import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TemplateService } from '../../services/template.service';
import { ControlTypeModel, TemplateModel, TemplateTypeModel, BusinessUnitModel, RoleModel } from '../../models/template.model';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { CommonModule } from '@angular/common';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { Router, RouterLink } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { ProjectStateService } from '../../services/project-state.service';
import { Toast } from "primeng/toast";
import { ProjectService } from '../../services/project.service';
import { TEMPLATE_TYPE } from '../../models/template.constants';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-templates',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    CardModule,
    DialogModule,
    ButtonModule,
    TextareaModule,
    InputTextModule,
    CheckboxModule,
    SelectModule,
    MultiSelectModule,
    ToggleSwitch,
    TooltipModule,
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    Toast
  ],
  templateUrl: './templates.html',
  styleUrl: './templates.css',
})
export class TemplateComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  // Expose constants to template
  readonly TEMPLATE_TYPE = TEMPLATE_TYPE;

  activeTab = '0';
  templates: TemplateModel[] = []; // Type 1 - Templates
  forms: TemplateModel[] = []; // Type 2 - Forms
  genericTemplates: TemplateModel[] = []; // Type 3 - Generic/Template List
  filteredTemplates: TemplateModel[] = [];
  filteredForms: TemplateModel[] = [];
  filteredGenericTemplates: TemplateModel[] = [];
  templateTypeList: TemplateTypeModel[] = [];
  roleList: RoleModel[] = [];
  businessUnitList: BusinessUnitModel[] = [];
  form!: FormGroup;
  showDialog = false;
  dialogMaximized = false;
  dialogOpening = false;
  isEdit = false;
  selectedTemplate: TemplateModel | null = null;
  currentTemplateType: number = TEMPLATE_TYPE.TEMPLATE; // Track which tab is adding/editing
  showCloneDialog = false;
  cloneTemplateName = '';
  cloneSourceTemplateId: number | null = null;
  cloneTemplateType: number = TEMPLATE_TYPE.TEMPLATE;
  expandedGroupsTemplates: { [key: string]: boolean } = {};
  expandedGroupsForms: { [key: string]: boolean } = {};
  expandedGroupsGeneric: { [key: string]: boolean } = {};
  constructor(
    private templateService: TemplateService,
    private projectService: ProjectService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private router: Router,
    private messageService: MessageService,
    private projectStateService: ProjectStateService,
  ) {}

  ngOnInit(): void {
    this.initForm({} as TemplateModel);
    this.loadTemplates(TEMPLATE_TYPE.TEMPLATE);
    this.loadForms(TEMPLATE_TYPE.FORMS);
    this.loadGenericTemplates(TEMPLATE_TYPE.GENERIC);
    this.loadTemplateTypes();
    this.loadRoles();
    this.loadBusinessUnits();
    this.projectStateService.pageSearch$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(search => {
      this.applySearch(search);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applySearch(search: string): void {
    const term = (search || '').trim().toLowerCase();
    if (!term) {
      this.filteredTemplates = [...this.templates];
      this.filteredForms = [...this.forms];
      this.filteredGenericTemplates = [...this.genericTemplates];
    } else {
      this.filteredTemplates = this.templates.filter(t =>
        (t.templateName || '').toLowerCase().includes(term) ||
        (t.shortCode || '').toLowerCase().includes(term)
      );
      this.filteredForms = this.forms.filter(t =>
        (t.templateName || '').toLowerCase().includes(term) ||
        (t.shortCode || '').toLowerCase().includes(term)
      );
      this.filteredGenericTemplates = this.genericTemplates.filter(t =>
        (t.templateName || '').toLowerCase().includes(term) ||
        (t.shortCode || '').toLowerCase().includes(term)
      );
    }
    this.cdr.markForCheck();
  }

  initForm(item: TemplateModel) {
    const isGenericTemplate = this.currentTemplateType === TEMPLATE_TYPE.GENERIC || item.typeId === TEMPLATE_TYPE.GENERIC;

    this.form = this.fb.group({
      templateId: [item.templateId || 0],
      templateName: [item.templateName || '', Validators.required],
      shortCode: [item.shortCode || '', [Validators.required, Validators.maxLength(16)]],
      description: [item.description || ''],
      typeId: [item.typeId || null, Validators.required],
      selectedRoleIds: [item.selectedRoleIds || [], isGenericTemplate ? [] : Validators.required],
      businessUnitId: [item.businessUnitId || null, isGenericTemplate ? [] : Validators.required],
      isActive: [item.isActive || true],
      createdAt: [item.createdAt || new Date()],
      updatedAt: [item.updatedAt || null]
    });
  }

  loadTemplates(templateTypeId: number) {
    this.templateService.getTemplateList(templateTypeId).subscribe((data: TemplateModel[]) => {
      this.templates = data;
      this.applySearch(this.projectStateService.getPageSearch());
    });
  }

  loadForms(templateTypeId: number) {
    this.templateService.getTemplateList(templateTypeId).subscribe((data: TemplateModel[]) => {
      this.forms = data;
      this.applySearch(this.projectStateService.getPageSearch());
    });
  }

  loadGenericTemplates(templateTypeId: number) {
    this.templateService.getTemplateList(templateTypeId).subscribe((data: TemplateModel[]) => {
      this.genericTemplates = data;
      this.applySearch(this.projectStateService.getPageSearch());
    });
  }

  loadTemplateTypes() {
    if (this.templateService.getTemplateTypeList) {
      this.templateService.getTemplateTypeList().subscribe((data: TemplateTypeModel[]) => this.templateTypeList = data);
    }
  }

  loadRoles() {
    this.projectService.getRoleList().subscribe((data: RoleModel[]) => {
      this.roleList = data;
      this.cdr.markForCheck();
    });
  }

  loadBusinessUnits() {
    this.templateService.getBusinessUnits().subscribe((data: BusinessUnitModel[]) => {
      this.businessUnitList = (data || []).map(x => ({
        ...x,
        displayName: x.displayName || x.name
      }));
      this.cdr.markForCheck();
    });
  }

  openAddDialog(templateType: number = TEMPLATE_TYPE.TEMPLATE) {
    this.isEdit = false;
    this.currentTemplateType = templateType;
    this.setActiveTabForType(templateType);
    const newTemplate = { typeId: templateType } as TemplateModel;
    this.initForm(newTemplate);
    this.dialogMaximized = false;
    this.dialogOpening = true;
    this.showDialog = true;
  }

  openEditDialog(template: TemplateModel, templateType: number = TEMPLATE_TYPE.TEMPLATE) {
    this.isEdit = true;
    this.currentTemplateType = templateType;
    this.setActiveTabForType(templateType);
    this.dialogMaximized = false;
    this.dialogOpening = true;
    this.initForm(template);

    // Load existing role associations for this template
    if (template.templateId) {
      this.templateService.getTemplateRoles(template.templateId).subscribe((roleIds: number[]) => {
        template.selectedRoleIds = roleIds;
        this.form.patchValue(template);
        this.cdr.markForCheck();
      });
    } else {
      this.form.patchValue(template);
    }

    this.showDialog = true;
  }

  openCloneDialog(template: TemplateModel, templateType: number = TEMPLATE_TYPE.TEMPLATE) {
    this.cloneSourceTemplateId = template.templateId || null;
    this.cloneTemplateType = templateType;
    this.cloneTemplateName = `${template.templateName || 'Template'}-copy`;
    this.showCloneDialog = true;
  }

  saveClone(): void {
    if (!this.cloneSourceTemplateId) {
      return;
    }
    const name = this.cloneTemplateName.trim();
    if (!name) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid Data', detail: 'Template name is required' });
      return;
    }

    this.templateService.cloneTemplate({
      sourceTemplateId: this.cloneSourceTemplateId,
      templateName: name
    }).subscribe({
      next: (newId) => {
        if (newId && newId > 0) {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Template cloned successfully' });
          if (this.cloneTemplateType === TEMPLATE_TYPE.TEMPLATE) {
            this.loadTemplates(TEMPLATE_TYPE.TEMPLATE);
          } else if (this.cloneTemplateType === TEMPLATE_TYPE.FORMS) {
            this.loadForms(TEMPLATE_TYPE.FORMS);
          } else if (this.cloneTemplateType === TEMPLATE_TYPE.GENERIC) {
            this.loadGenericTemplates(TEMPLATE_TYPE.GENERIC);
          }
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to clone template' });
        }
        this.showCloneDialog = false;
        this.cloneTemplateName = '';
        this.cloneSourceTemplateId = null;
      },
      error: (error) => {
        console.error('Error cloning template:', error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to clone template' });
      }
    });
  }

  private setActiveTabForType(templateType: number) {
    if (templateType === TEMPLATE_TYPE.FORMS) {
      this.activeTab = '1';
      return;
    }
    if (templateType === TEMPLATE_TYPE.GENERIC) {
      this.activeTab = '2';
      return;
    }
    this.activeTab = '0';
  }

  getTemplateTypeName(typeId: number | null): string {
    const type = this.templateTypeList.find(t => t.templateTypeId === typeId);
    return type ? type.typeName : 'N/A';
  }

  getBusinessUnitName(businessUnitId: number | null | undefined): string {
    if (!businessUnitId) return 'N/A';
    const unit = this.businessUnitList.find(b => b.id === businessUnitId);
    return unit ? (unit.displayName || unit.name) : 'N/A';
  }

  saveTemplate() {
    if (this.form.invalid) return;
    const template = this.form.value as TemplateModel;
    console.log('Saving template:', template);
    if (this.isEdit && template.templateId > 0) {
      this.templateService.updateTemplate(template).subscribe((res) => {
        if (res) {
          // Update the appropriate list based on template type
          this.updateTemplateInList(template);
          this.ensureGroupExpandedForTemplate(template);
          this.reloadTemplateList(template.typeId || this.currentTemplateType);
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Template updated successfully' });
        }
        this.showDialog = false;
      });
    } else {
      this.templateService.addTemplate(template).subscribe((res) => {
        if (res && res > 0) {
          template.templateId = res;
          // Add to the appropriate list based on template type
          this.addTemplateToList(template);
          this.ensureGroupExpandedForTemplate(template);
          this.reloadTemplateList(template.typeId || this.currentTemplateType);
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Template added successfully' });
        }
        this.showDialog = false;
      });
    }
  }

  onTemplateDialogShow(dialog: { maximize: () => void }): void {
    dialog.maximize();
  }

  onTemplateDialogMaximize(event: { maximized: boolean }): void {
    this.dialogMaximized = !!event?.maximized;
    this.dialogOpening = false;
  }

  private reloadTemplateList(typeId: number) {
    if (typeId === TEMPLATE_TYPE.TEMPLATE) {
      this.loadTemplates(TEMPLATE_TYPE.TEMPLATE);
      return;
    }
    if (typeId === TEMPLATE_TYPE.FORMS) {
      this.loadForms(TEMPLATE_TYPE.FORMS);
      return;
    }
    this.loadGenericTemplates(TEMPLATE_TYPE.GENERIC);
  }

  private ensureGroupExpandedForTemplate(template: TemplateModel): void {
    const key = String(template.businessUnitId ?? 'N/A');
    const typeId = template.typeId || this.currentTemplateType;
    if (typeId === TEMPLATE_TYPE.FORMS) {
      this.expandedGroupsForms[key] = true;
    } else if (typeId === TEMPLATE_TYPE.GENERIC) {
      this.expandedGroupsGeneric[key] = true;
    } else {
      this.expandedGroupsTemplates[key] = true;
    }
  }

  isGroupExpanded(groupType: 'templates' | 'forms' | 'generic', businessUnitId: number | null | undefined): boolean {
    const key = String(businessUnitId ?? 'N/A');
    const target = this.getExpandedGroupRef(groupType);
    return !!target[key];
  }

  toggleGroup(groupType: 'templates' | 'forms' | 'generic', businessUnitId: number | null | undefined): void {
    const key = String(businessUnitId ?? 'N/A');
    const target = this.getExpandedGroupRef(groupType);
    target[key] = !target[key];
  }

  private getExpandedGroupRef(groupType: 'templates' | 'forms' | 'generic') {
    if (groupType === 'forms') return this.expandedGroupsForms;
    if (groupType === 'generic') return this.expandedGroupsGeneric;
    return this.expandedGroupsTemplates;
  }

  toggleTemplateActive(template: TemplateModel): void {
    // ngModel has already updated template.isActive to the new value
    const newValue = !!template.isActive;
    const statusText = newValue ? 'activate' : 'deactivate';

    this.templateService.updateTemplateActive(template.templateId, newValue).subscribe({
      next: (success) => {
        if (success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Template has been ${statusText}d successfully`
          });
          this.cdr.markForCheck();
        } else {
          template.isActive = !newValue;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to ${statusText} template`
          });
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        console.error('Error updating template active status:', error);
        template.isActive = !newValue;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to ${statusText} template. Please try again.`
        });
        this.cdr.markForCheck();
      }
    });
  }

  private updateTemplateInList(template: TemplateModel) {
    const typeId = template.typeId || this.currentTemplateType;
    if (typeId === TEMPLATE_TYPE.TEMPLATE) {
      const index = this.templates.findIndex(t => t.templateId === template.templateId);
      if (index !== -1) this.templates[index] = template;
    } else if (typeId === TEMPLATE_TYPE.FORMS) {
      const index = this.forms.findIndex(t => t.templateId === template.templateId);
      if (index !== -1) this.forms[index] = template;
    } else if (typeId === TEMPLATE_TYPE.GENERIC) {
      const index = this.genericTemplates.findIndex(t => t.templateId === template.templateId);
      if (index !== -1) this.genericTemplates[index] = template;
    }
  }

  private addTemplateToList(template: TemplateModel) {
    const typeId = template.typeId || this.currentTemplateType;
    if (typeId === TEMPLATE_TYPE.TEMPLATE) {
      this.templates.push(template);
    } else if (typeId === TEMPLATE_TYPE.FORMS) {
      this.forms.push(template);
    } else if (typeId === TEMPLATE_TYPE.GENERIC) {
      this.genericTemplates.push(template);
    }
  }

  deleteTemplate(template: TemplateModel, templateType: number = TEMPLATE_TYPE.TEMPLATE) {
    if (template.templateId && confirm('Delete this template?')) {
      this.templateService.deleteTemplate(template.templateId).subscribe({
        next: () => {
          const typeId = template.typeId || templateType;
          this.reloadTemplateList(typeId);
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Template deleted successfully' });
        },
        error: (error) => {
          console.error('Error deleting template:', error);
          const detail = error?.error?.message || error?.message || 'Failed to delete template. Please try again.';
          this.messageService.add({ severity: 'error', summary: 'Error', detail });
        }
      });
    }
  }

  navigateToChecksheet(templateId: number): void {
    this.router.navigate(['/checksheet', templateId], { queryParams: { source: 'templates' } });
    console.log(`Navigating to checksheet for template ID: ${templateId}`);
  }

  get activeTabType(): number {
    if (this.activeTab === '1') return TEMPLATE_TYPE.FORMS;
    if (this.activeTab === '2') return TEMPLATE_TYPE.GENERIC;
    return TEMPLATE_TYPE.TEMPLATE;
  }

  getGroupedData(list: TemplateModel[]): { businessUnitId: number | null; name: string; items: TemplateModel[] }[] {
    const groupMap = new Map<string, { businessUnitId: number | null; items: TemplateModel[] }>();
    for (const t of list) {
      const buId = t.businessUnitId ?? null;
      const key = String(buId ?? 'N/A');
      if (!groupMap.has(key)) {
        groupMap.set(key, { businessUnitId: buId, items: [] });
      }
      groupMap.get(key)!.items.push(t);
    }
    return Array.from(groupMap.values())
      .sort((a, b) => {
        const nameA = a.businessUnitId ? this.getBusinessUnitName(a.businessUnitId) : 'Unassigned';
        const nameB = b.businessUnitId ? this.getBusinessUnitName(b.businessUnitId) : 'Unassigned';
        return nameA.localeCompare(nameB);
      })
      .map(g => ({
        businessUnitId: g.businessUnitId,
        name: g.businessUnitId ? this.getBusinessUnitName(g.businessUnitId) : 'Unassigned',
        items: g.items.sort((a, b) =>
          (a.shortCode || a.templateName || '').localeCompare(b.shortCode || b.templateName || '')
        )
      }));
  }

  formatDate(date: string | Date | null | undefined): string {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    const dateStr = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
    const timeStr = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
    return `${dateStr} | ${timeStr}`;
  }

}
